/**
 * LiveRecordingDialog — the in-class recording experience.
 *
 * Extracted from LecturesPage.js so the page-level component stays focused
 * on data orchestration. State and handlers still live in the parent — the
 * dialog accepts a single consolidated `s` prop (a state bag) so the
 * extraction is mechanical and reversible. A future iteration will
 * introduce a proper LiveRecordingContext.
 *
 * @typedef {Object} LiveRecordingState
 * Recording context (from useRecording)
 * @property {boolean} recording
 * @property {boolean} transcribing
 * @property {number} recordingTime
 * @property {Array<{text: string, ts?: number}>} liveTranscript
 * @property {string} interimText
 * @property {boolean} speechSupported
 * @property {boolean} isListening
 * @property {Array<{ts: number, mode: string}>} confusedMarkers
 * @property {Array<{ts: number, label: string}>} bookmarks
 * @property {string|null} lastVoiceCommand
 * @property {Blob[]} audioChunks
 * @property {'mic'|'tab'|null} recordingSource
 * @property {(seconds: number) => string} formatTime
 * @property {(label: string) => void} addBookmark
 * @property {(mode?: string) => void} markConfused
 *
 * Transcription context (from useTranscription)
 * @property {'cloud'|'local'} transcriptionMode
 * @property {(m: 'cloud'|'local') => void} setTranscriptionMode
 * @property {boolean} localModelReady
 * @property {boolean} localModelLoading
 * @property {string} localModelProgress
 * @property {boolean} localModelFailed
 * @property {() => Promise<void>} initLocalWhisper
 *
 * Dialog-local state & refs
 * @property {string|null} recordedAudioUrl
 * @property {Blob|null} recordedAudioBlob
 * @property {string} finalTranscriptText
 * @property {(v: string) => void} setFinalTranscriptText
 * @property {Array<{id: string, name: string, url: string}>} attachedFiles
 * @property {(v: any) => void} setAttachedFiles
 * @property {boolean} attachUploading
 * @property {React.MutableRefObject<HTMLInputElement|null>} attachFileRef
 * @property {string[]} titleSuggestions
 * @property {(v: string[]) => void} setTitleSuggestions
 * @property {boolean} suggestingTitle
 * @property {boolean} confusedPopupOpen
 * @property {string} confusedExplanation
 * @property {boolean} confusedLoading
 * @property {{subject_id: string, title: string, topic: string, notes: string, is_public: boolean}} formData
 * @property {(v: any) => void} setFormData
 * @property {boolean} autoGenerate
 * @property {(v: boolean) => void} setAutoGenerate
 * @property {boolean} submitting
 * @property {Array<{id: string, name: string, color?: string}>} subjects
 * @property {(v: any) => void} setSubjects
 * @property {React.MutableRefObject<HTMLDivElement|null>} transcriptContainerRef
 * @property {boolean} liveRecordingOpen
 * @property {(v: boolean) => void} setLiveRecordingOpen
 *
 * Handlers
 * @property {() => void} minimizeRecording
 * @property {() => Promise<void>} handleStopRecording
 * @property {() => void} startLiveRecording
 * @property {() => Promise<void>} startTabCapture
 * @property {(mode?: string) => Promise<void>} handleConfusedButtonClick
 * @property {() => void} closeConfusedPopup
 * @property {() => void} downloadAudio
 * @property {() => void} downloadTranscript
 * @property {() => Promise<void>} suggestTitles
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => Promise<void>} handleAttachFile
 * @property {(e: React.FormEvent) => Promise<void>} handleSubmit
 * @property {(e: React.TouchEvent) => void} handleRecordingTouchStart
 * @property {(e: React.TouchEvent) => void} handleRecordingTouchEnd
 */
import { AnimatePresence, motion } from 'framer-motion';
import {
  Mic, Shield, Cloud, Wifi, WifiOff, Zap, Check, Clock, Radio, Loader2,
  Minimize2, Play, Pause, HelpCircle, Bookmark, BookOpen, X, Volume2,
  AlertCircle, Sparkles, FileText, Download, Upload,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import SubjectSelectWithInlineCreate from '../SubjectSelectWithInlineCreate';
import CloudBackupHint from './CloudBackupHint';

/**
 * @param {{ s: LiveRecordingState }} props
 */
const LiveRecordingDialog = ({ s }) => (
      <Dialog open={s.liveRecordingOpen} onOpenChange={(open) => {
        if (!open && s.recording) {
          // Don't stop recording when closing modal — it continues in background via FloatingRecorder
        }
        s.setLiveRecordingOpen(open);
      }}>
        <DialogContent 
          className="sm:max-w-4xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] rounded-none sm: max-h-[100vh] sm:max-h-[90vh] overflow-y-auto w-full h-full sm:h-auto sm:w-auto" 
          data-testid="live-recording-modal"
          onTouchStart={s.handleRecordingTouchStart}
          onTouchEnd={s.handleRecordingTouchEnd}
        >
          {/* Swipe indicator for mobile */}
          {s.recording && (
            <div className="sm:hidden flex justify-center pt-2 pb-0">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>
          )}
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold text-[var(--isp-heading)] flex flex-wrap items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${s.recording ? 'bg-red-500 recording-pulse' : 'bg-slate-700'}`}>
                <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="flex-1 min-w-0">{s.recordingSource === 'tab' && s.recording ? 'Tab Audio Capture' : 'Live Recording'}</span>
              {s.recording && (
                <Button aria-label="Minimize to floating widget"
                  variant="ghost"
                  size="icon"
                  onClick={s.minimizeRecording}
                  className="h-8 w-8 rounded-lg text-white/50 hover:text-white hover:bg-white/10 sm:hidden"
                  data-testid="minimize-recording-btn"
                  title="Minimize to floating widget"
                >
                  <Minimize2 className="w-4 h-4" />
                </Button>
              )}
              {s.transcriptionMode === 'local' ? (
                <span className="text-[10px] sm:text-xs font-normal bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full flex items-center gap-1 shrink-0" data-testid="privacy-badge">
                  <Shield className="w-3 h-3" />
                  Private Mode
                </span>
              ) : (
                <span className="text-[10px] sm:text-xs font-normal bg-[#002FA7]/20 text-[#002FA7] px-2 py-1 rounded-full flex items-center gap-1 shrink-0" data-testid="cloud-badge">
                  <Cloud className="w-3 h-3" />
                  Best Quality
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4 pb-24 sm:pb-4">
            {/* Friendly Recording Mode Selector */}
            {!s.recording && (
              <div className="space-y-3" data-testid="transcription-mode-selector">
                <p className="text-sm text-[var(--isp-body)] text-center">How should we transcribe your recording?</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Cloud Mode — Recommended */}
                  <button
                    onClick={() => s.setTranscriptionMode('cloud')}
                    className={`relative p-4  text-left transition-all border-2 ${
                      s.transcriptionMode === 'cloud'
                        ? 'border-[#002FA7] bg-[#002FA7]/5 shadow-lg shadow-[#002FA7]/10'
                        : 'border-[var(--isp-glass-border)] hover:border-[#002FA7]/30 bg-[var(--isp-glass-inner)]'
                    }`}
                    data-testid="best-quality-mode"
                  >
                    <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-[#002FA7] text-white text-[9px] font-bold rounded-full uppercase tracking-wider">Recommended</div>
                    <div className="flex items-start gap-3 mt-1">
                      <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center shrink-0">
                        <Cloud className="w-5 h-5 text-[#002FA7]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[var(--isp-heading)]">Best Quality</p>
                        <p className="text-xs text-[var(--isp-body)] mt-0.5 leading-relaxed">
                          Uses our servers for the most accurate transcription. Works with any accent or language.
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[10px] text-emerald-500 flex items-center gap-1"><Wifi className="w-3 h-3" /> Needs internet</span>
                          <span className="text-[10px] text-[#002FA7] flex items-center gap-1"><Zap className="w-3 h-3" /> Fast & accurate</span>
                        </div>
                      </div>
                    </div>
                    {s.transcriptionMode === 'cloud' && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#002FA7] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>

                  {/* Local Mode — Private */}
                  <button
                    onClick={() => {
                      if (s.localModelFailed) {
                        toast.info('This option is not available on your device right now. Use "Best Quality" instead — it works great!');
                        return;
                      }
                      s.setTranscriptionMode('local');
                      if (!s.localModelReady && !s.localModelLoading) s.initLocalWhisper();
                    }}
                    className={`relative p-4  text-left transition-all border-2 ${
                      s.transcriptionMode === 'local' && !s.localModelFailed
                        ? 'border-emerald-500 bg-emerald-500/5 shadow-lg shadow-emerald-500/10'
                        : s.localModelFailed
                          ? 'border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] opacity-50'
                          : 'border-[var(--isp-glass-border)] hover:border-emerald-500/30 bg-[var(--isp-glass-inner)]'
                    }`}
                    data-testid="private-mode-btn"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <Shield className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[var(--isp-heading)]">
                          Private Mode {s.localModelFailed && <span className="text-[10px] text-[var(--isp-muted)] font-normal">(Unavailable)</span>}
                        </p>
                        <p className="text-xs text-[var(--isp-body)] mt-0.5 leading-relaxed">
                          {s.localModelFailed
                            ? 'Not supported on this device. Use Best Quality instead.'
                            : 'Everything stays on your device. No data leaves your phone or laptop.'
                          }
                        </p>
                        {!s.localModelFailed && (
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-[10px] text-emerald-500 flex items-center gap-1"><Shield className="w-3 h-3" /> 100% private</span>
                            <span className="text-[10px] text-[var(--isp-muted)] flex items-center gap-1"><WifiOff className="w-3 h-3" /> Works offline</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {s.transcriptionMode === 'local' && !s.localModelFailed && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                </div>

                {/* Simple explainer */}
                <p className="text-[10px] text-center text-[var(--isp-muted)]">
                  {s.transcriptionMode === 'cloud'
                    ? 'Your recording will be sent to our secure servers for transcription. Results appear in seconds.'
                    : 'Your audio never leaves your device. Transcription runs locally using AI on your browser.'
                  }
                </p>
              </div>
            )}

            {/* Local model loading indicator */}
            {s.localModelLoading && (
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                <div>
                  <p className="text-sm font-medium text-emerald-400">Setting up Private Mode...</p>
                  <p className="text-[10px] text-emerald-400/70">{s.localModelProgress || 'Downloading the AI model to your device. This only happens once.'}</p>
                </div>
              </div>
            )}

            {/* Recording controls */}
            <div className="p-4 bg-[var(--isp-glass)] backdrop-blur-xl rounded-xl border border-[var(--isp-glass-border)]">
              <div className="flex items-center gap-3 mb-3">
                <div className={`flex items-center gap-2 ${s.recording ? 'text-red-400' : 'text-[var(--isp-body)]'}`}>
                  {s.recording && <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />}
                  <Clock className="w-5 h-5" />
                  <span className="font-mono text-lg">{s.formatTime(s.recordingTime)}</span>
                </div>
                {s.recording && s.isListening && (
                  <div className="flex items-center gap-2 text-green-400">
                    <Radio className="w-4 h-4 animate-pulse" />
                    <span className="text-sm">Listening...</span>
                  </div>
                )}
                {s.transcribing && (
                  <div className="flex items-center gap-2 text-purple-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {!s.recording ? (
                  <div className="w-full space-y-3">
                    <Button
                      onClick={s.startLiveRecording}
                      className="gap-3 isp-btn-primary  w-full h-14 text-base font-semibold"
                      data-testid="start-recording-btn"
                    >
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-4 h-4" />
                      </div>
                      Tap to Start Recording
                    </Button>
                    <p className="text-[10px] text-center text-[var(--isp-muted)]">
                      Press the button, then start speaking. We'll transcribe everything automatically.
                    </p>
                  </div>
                ) : (
                  <div className="w-full space-y-2">
                    <Button
                      onClick={s.handleStopRecording}
                      variant="destructive"
                      className="gap-2 rounded-xl w-full h-12 text-base font-semibold"
                      data-testid="stop-recording-btn"
                    >
                      <Pause className="w-5 h-5" />
                      Stop & Save Recording
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => s.handleConfusedButtonClick('explain')}
                        disabled={s.confusedLoading}
                        className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl flex-1 h-10 text-sm"
                        data-testid="confused-explain-btn"
                      >
                        <HelpCircle className="w-4 h-4" />
                        I'm Confused
                      </Button>
                      <Button
                        onClick={() => s.addBookmark('highlight', 'Highlight')}
                        variant="outline"
                        className="gap-2 rounded-xl border-blue-500/30 text-blue-300 hover:bg-blue-500/10 flex-1 h-10 text-sm"
                        data-testid="highlight-btn"
                      >
                        <Bookmark className="w-4 h-4" />
                        Highlight
                      </Button>
                      <Button
                        onClick={() => s.addBookmark('important', 'Important')}
                        variant="outline"
                        className="gap-2 rounded-xl border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-10 text-sm"
                        data-testid="important-btn"
                      >
                        <Zap className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => s.addBookmark('topic', 'New Topic')}
                        variant="outline"
                        className="gap-2 rounded-xl border-purple-500/30 text-purple-300 hover:bg-purple-500/10 h-10 text-sm"
                        data-testid="new-topic-btn"
                      >
                        <BookOpen className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Voice Commands Indicator */}
                    {s.isListening && (
                      <div className="p-2.5 bg-emerald-500/5 border border-emerald-500/20 rounded-xl" data-testid="voice-commands-hint">
                        <p className="text-[10px] text-emerald-400 font-medium mb-1 flex items-center gap-1">
                          <Radio className="w-3 h-3 animate-pulse" /> Voice Commands Active
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {['"I\'m confused"', '"Highlight this"', '"Important"', '"New topic"'].map(cmd => (
                            <span key={cmd} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300/80">{cmd}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Voice Command Flash Notification */}
                    {s.lastVoiceCommand && (
                      <div className={`p-2.5 rounded-xl text-sm font-medium flex items-center gap-2 animate-pulse ${
                        s.lastVoiceCommand.type === 'confused' ? 'bg-amber-500/15 text-amber-400' :
                        s.lastVoiceCommand.type === 'highlight' ? 'bg-blue-500/15 text-blue-400' :
                        s.lastVoiceCommand.type === 'important' ? 'bg-yellow-500/15 text-yellow-400' :
                        'bg-purple-500/15 text-purple-400'
                      }`} data-testid="voice-command-flash">
                        {s.lastVoiceCommand.label} at {s.lastVoiceCommand.timestamp}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Bookmarks Display */}
            {s.bookmarks.length > 0 && (
              <div className="p-3 bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] rounded-xl" data-testid="bookmarks-bar">
                <p className="text-xs font-semibold text-[var(--isp-heading)] mb-2">Bookmarks ({s.bookmarks.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {s.bookmarks.map((bm, idx) => (
                    <span key={idx} className={`px-2 py-1 text-[10px] rounded-lg flex items-center gap-1 ${
                      bm.type === 'confused' ? 'bg-amber-500/20 text-amber-300' :
                      bm.type === 'highlight' ? 'bg-blue-500/20 text-blue-300' :
                      bm.type === 'important' ? 'bg-yellow-500/20 text-yellow-300' :
                      bm.type === 'topic' ? 'bg-purple-500/20 text-purple-300' :
                      'bg-white/10 text-[var(--isp-muted)]'
                    }`}>
                      {bm.label} · {bm.timestamp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* I'm Confused Popup */}
            <AnimatePresence>
              {s.confusedPopupOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute inset-0 bg-[var(--isp-glass)]/95 backdrop-blur-sm  p-6 flex flex-col z-50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                        <HelpCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">AI Explanation</h3>
                        <p className="text-xs text-[var(--isp-body)]">Recording paused at {s.formatTime(s.recordingTime)}</p>
                      </div>
                    </div>
                    <Button aria-label="Close"
                      onClick={s.closeConfusedPopup}
                      variant="ghost"
                      size="icon"
                      className="text-[var(--isp-body)] hover:text-[var(--isp-heading)]"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {s.confusedLoading ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Loader2 className="w-10 h-10 animate-spin text-amber-500 mb-4" />
                        <p className="text-[var(--isp-body)]">Analyzing the content...</p>
                        <p className="text-xs text-[var(--isp-muted)] mt-1">Getting a simple explanation for you</p>
                      </div>
                    ) : (
                      <div className="prose prose-invert max-w-none">
                        <div className="p-4 bg-[var(--isp-glass)] backdrop-blur-xl rounded-xl border border-[var(--isp-glass-border)]">
                          <div className="whitespace-pre-wrap text-[var(--isp-heading)] text-sm leading-relaxed">
                            {s.confusedExplanation}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-4 pt-4 border-t border-[var(--isp-glass-border)]">
                    <Button
                      onClick={() => {
                        s.handleConfusedButtonClick('mark');
                        s.closeConfusedPopup();
                      }}
                      variant="outline"
                      className="flex-1 gap-2 rounded-xl border-white/20"
                    >
                      <Bookmark className="w-4 h-4" />
                      Mark for Later
                    </Button>
                    <Button
                      onClick={s.closeConfusedPopup}
                      className="flex-1 gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500"
                    >
                      <Play className="w-4 h-4" />
                      Got it! Resume
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live transcript display */}
            <div className="space-y-2">
              <Label className="text-[var(--isp-body)] flex items-center gap-2">
                <Volume2 className="w-4 h-4" />
                Live Transcript
                {!s.speechSupported && (
                  <span className="text-xs text-orange-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Browser doesn't support live transcription
                  </span>
                )}
              </Label>
              <div 
                ref={s.transcriptContainerRef}
                className="h-64 p-4 bg-[var(--isp-glass-inner)] rounded-xl border border-[var(--isp-glass-border)] overflow-y-auto"
                data-testid="live-transcript-container"
              >
                {s.liveTranscript.length === 0 && !s.interimText ? (
                  <div className="flex flex-col items-center justify-center h-full text-[var(--isp-muted)]">
                    <Mic className="w-12 h-12 mb-3 opacity-50" />
                    <p>{s.recording ? 'Listening... Speak clearly' : 'Start recording to see live transcription'}</p>
                    {s.recording && s.speechSupported && (
                      <p className="text-xs mt-2 text-purple-400">Words appear as you speak!</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <AnimatePresence>
                      {s.liveTranscript.map((item, index) => (
                        <motion.div
                          key={`transcript-${item.timestamp}-${index}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex gap-3"
                        >
                          <span className="text-xs text-purple-400 font-mono w-12 flex-shrink-0 pt-1">
                            {item.timestamp}
                          </span>
                          <p className={`text-sm leading-relaxed ${item.isWhisper ? 'text-green-300' : 'text-[var(--isp-heading)]'}`}>
                            {item.text}
                            {item.isWhisper && (
                              <span className="ml-2 text-xs text-green-400/60">(Whisper refined)</span>
                            )}
                          </p>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    
                    {/* Show interim (partial) transcription */}
                    {s.interimText && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex gap-3"
                      >
                        <span className="text-xs text-orange-400 font-mono w-12 flex-shrink-0 pt-1">
                          {s.formatTime(s.recordingTime)}
                        </span>
                        <p className="text-sm leading-relaxed text-[var(--isp-body)] italic">
                          {s.interimText}
                          <span className="inline-block w-1 h-4 bg-orange-400 animate-pulse ml-1 align-middle" />
                        </p>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Info about the hybrid approach */}
            {s.recording && (
              <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-xl border border-blue-500/20 text-blue-300 text-sm">
                <Radio className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Hybrid Transcription Active</p>
                  <p className="text-blue-300/70 text-xs mt-1">
                    Live text appears as you speak (Web Speech API). When you stop, Whisper AI provides an accurate final transcript.
                  </p>
                </div>
              </div>
            )}

            {/* Save form */}
            {s.liveTranscript.length > 0 && !s.recording && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4 p-4 bg-[#002FA7]/5 rounded-xl border-2 border-[#002FA7]/20"
              >
                {/* Post-Recording Assets */}
                {(s.recordedAudioUrl || s.finalTranscriptText) && (
                  <div className="space-y-4 mb-4" data-testid="post-recording-save">
                    {/* Save Options Header */}
                    <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 ">
                      <h4 className="font-bold text-[var(--isp-heading)] text-lg flex items-center gap-2 mb-1">
                        <Check className="w-5 h-5 text-emerald-500" />
                        Recording Complete!
                      </h4>
                      <p className="text-sm text-[var(--isp-body)]">Your lecture has been captured. Choose where to save it:</p>
                    </div>

                    {/* Save to Device Section */}
                    <div className="p-4 bg-[var(--isp-glass-inner)]  border border-[var(--isp-glass-border)]" data-testid="save-to-device-section">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <Download className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[var(--isp-heading)]">Save to Your Device</p>
                          <p className="text-[10px] text-[var(--isp-muted)]">Download files directly to your phone or computer</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {s.recordedAudioUrl && (
                          <Button size="sm" onClick={s.downloadAudio}
                            className="gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid="download-audio-btn">
                            <Download className="w-3.5 h-3.5" /> Download Audio
                          </Button>
                        )}
                        {s.finalTranscriptText && (
                          <Button size="sm" onClick={s.downloadTranscript}
                            className="gap-2 rounded-xl bg-[#002FA7] hover:bg-[#00227A] text-white text-xs" data-testid="download-transcript-btn">
                            <Download className="w-3.5 h-3.5" /> Download Transcript (.txt)
                          </Button>
                        )}
                      </div>
                      {s.transcriptionMode === 'local' && (
                        <p className="text-[10px] text-emerald-500 mt-2 flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Private Mode: Your audio was processed on-device and never left your phone.
                        </p>
                      )}
                    </div>

                    {/* Audio Player */}
                    {s.recordedAudioUrl && (
                      <div className="px-4 py-3 bg-[var(--isp-glass-inner)] rounded-xl border border-[var(--isp-glass-border)]" data-testid="post-recording-audio">
                        <p className="text-xs text-[var(--isp-muted)] mb-2 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Listen to your recording:</p>
                        <audio controls src={s.recordedAudioUrl} className="w-full h-10" preload="metadata" />
                      </div>
                    )}

                    {/* Save to iStudyPro Cloud */}
                    <div className="p-4 bg-[#002FA7]/5  border-2 border-[#002FA7]/20" data-testid="save-to-cloud-section">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                          <Cloud className="w-4 h-4 text-[#002FA7]" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[var(--isp-heading)]">Save to iStudyPro</p>
                          <p className="text-[10px] text-[var(--isp-muted)]">Save to your library for AI flashcards, study guides, and exam prep</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-[#002FA7] mb-3 ml-10">Fill in the details below and tap "Save Lecture" to keep it in your library.</p>
                      {/* iter191 — contextual cloud-storage nudge.
                          Removed standalone OAuth buttons from SubjectsPage
                          and surface the prompt here, where it's actually
                          relevant (about to save a recording). */}
                      <CloudBackupHint />
                    </div>

                    {/* Editable Transcript */}
                    {s.finalTranscriptText && (
                      <div className="p-3 bg-[var(--isp-glass-inner)] rounded-xl border border-[var(--isp-glass-border)]" data-testid="post-recording-transcript">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-[#22C55E]" />
                          <span className="text-sm text-[#22C55E] font-medium">Transcript Preview</span>
                          <span className="text-[10px] text-[var(--isp-muted)]">(you can edit before saving)</span>
                        </div>
                        <Textarea
                          value={s.finalTranscriptText}
                          onChange={(e) => s.setFinalTranscriptText(e.target.value)}
                          rows={4}
                          className="w-full bg-[var(--isp-glass)] border-[var(--isp-glass-border)] rounded-lg text-sm text-[var(--isp-heading)] leading-relaxed resize-y"
                          data-testid="editable-transcript"
                          placeholder="Review and edit the transcription..."
                        />
                      </div>
                    )}
                  </div>
                )}

                <h3 className="font-semibold text-[var(--isp-heading)] flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-[#002FA7]" />
                  Save Your Lecture
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[var(--isp-body)]">Subject *</Label>
                    <SubjectSelectWithInlineCreate
                      value={s.formData.subject_id}
                      onChange={(v) => s.setFormData({ ...s.formData, subject_id: v })}
                      subjects={s.subjects}
                      onSubjectCreated={(created) => s.setSubjects((prev) => [created, ...prev])}
                      testidPrefix="save-subject-select"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[var(--isp-body)]">Title *</Label>
                      {(s.finalTranscriptText || s.formData.transcript) && (
                        <button
                          type="button"
                          onClick={s.suggestTitles}
                          disabled={s.suggestingTitle || ((s.finalTranscriptText || s.formData.transcript || '').trim().length < 20)}
                          className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#002FA7] hover:text-[#00227A] disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid="suggest-title-btn"
                          title={(s.finalTranscriptText || s.formData.transcript || '').trim().length < 20 ? 'Need a bit more transcript' : 'AI suggests a title'}
                        >
                          {s.suggestingTitle ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</>
                          ) : (
                            <><Sparkles className="w-3 h-3" /> Suggest title</>
                          )}
                        </button>
                      )}
                    </div>
                    <Input placeholder="Lecture title" value={s.formData.title} onChange={(e) => s.setFormData({ ...s.formData, title: e.target.value })}
                      className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder-[var(--isp-muted)]"
                      data-testid="save-title-input"
                    />
                    {s.titleSuggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid="title-suggestions">
                        {s.titleSuggestions.map((t, i) => (
                          <button
                            key={`${i}-${t}`}
                            type="button"
                            onClick={() => {
                              s.setFormData((f) => ({ ...f, title: t }));
                              s.setTitleSuggestions([]);
                            }}
                            className="px-2.5 py-1 text-[11px] bg-[#002FA7]/10 hover:bg-[#002FA7]/20 text-[#002FA7] border border-[#002FA7]/30 rounded-full transition-colors"
                            data-testid={`title-suggestion-${i}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Attach Files */}
                <div className="space-y-2">
                  <Label className="text-[var(--isp-body)] flex items-center gap-2"><Upload className="w-4 h-4" /> Attach Files (slides, PDFs)</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => s.attachFileRef.current?.click()} disabled={s.attachUploading}
                      className="gap-2 rounded-lg border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)]" data-testid="attach-file-btn">
                      {s.attachUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {s.attachUploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                    <input ref={s.attachFileRef} type="file" accept=".pdf,.txt,.md,.csv,.doc,.docx,.pptx,.png,.jpg,.jpeg" onChange={s.handleAttachFile} className="hidden" data-testid="attach-file-input" />
                  </div>
                  {s.attachedFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {s.attachedFiles.map((f, i) => (
                        <span key={f.name || i} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#002FA7]/10 text-[#002FA7] text-xs border border-[#002FA7]/20">
                          <FileText className="w-3 h-3" /> {f.name}
                          <button onClick={() => s.setAttachedFiles(prev => prev.filter((_, idx) => idx !== i))} className="ml-1 hover:text-red-400">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 cursor-pointer" data-testid="auto-generate-toggle-live">
                  <input type="checkbox" checked={s.autoGenerate} onChange={(e) => s.setAutoGenerate(e.target.checked)} className="w-4 h-4 rounded accent-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-[var(--isp-amber)]">Auto-generate flashcards</p>
                    <p className="text-xs text-[var(--isp-muted)]">Create study cards from this recording</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-xl bg-[#002FA7]/10 border border-[#002FA7]/20 cursor-pointer" data-testid="make-public-toggle-live">
                  <input type="checkbox" checked={s.formData.is_public} onChange={(e) => s.setFormData({ ...s.formData, is_public: e.target.checked })} className="w-4 h-4 rounded accent-[#002FA7]" />
                  <div>
                    <p className="text-sm font-medium text-[#002FA7]">Make Public</p>
                    <p className="text-xs text-[var(--isp-muted)]">Share to the discovery feed</p>
                  </div>
                </label>

                <Button onClick={s.handleSubmit} disabled={s.submitting || !s.formData.subject_id || !s.formData.title}
                  className="w-full gap-2 isp-btn-primary rounded-xl h-12 text-base font-semibold">
                  {s.submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>) : (<><Sparkles className="w-4 h-4" /> Save & Generate AI Summary</>)}
                </Button>
              </motion.div>
            )}
          </div>
        </DialogContent>
      </Dialog>
);

export default LiveRecordingDialog;
